import { state, filters } from '../state';
import type { Item, HomeOptionAction } from '../types';
import type { Quat } from '../utils/quaternion';
import { dom } from '../dom';
import {
  CX,
  CY,
  HOME_OPTIONS_ENTER_MS,
  HOME_OPTIONS_ZOOM_MS,
  HOME_OPTIONS_EXIT_MS,
  HOME_OPTIONS_REMOVE_DELAY_FRAC,
  HOME_OPTIONS_ZOOM_OUT_MS,
  HOME_OPTIONS_WORLD_ZOOM,
  HOME_OPTIONS_CAMERA_SMOOTHING,
  HOME_OPTIONS_HIDE_STAGGER_SPAN,
  HOME_OPTIONS_HIDE_FADE_SPAN,
  HOME_OPTIONS_TWIST_THRESHOLD_DEG,
  HOME_OPTIONS_TWIST_DEADZONE_DEG,
  HOME_EDIT_DRAG_WORLD_LERP,
  HOME_EDIT_DRAG_SCALE_LERP,
  OPTIONS_LIST_LEFT_PX,
  OPTIONS_LIST_CENTER_Y_PX,
  OPTIONS_OPTION_GAP_PX,
  OPTIONS_OPTION_LINE_HEIGHT_SELECTED_PX,
  OPTIONS_OPTION_LINE_HEIGHT_UNSELECTED_PX,
} from '../config';
import { clamp, lerp, smoothstep, easeOutCubic, easeInOutCubic, easeOutBack } from '../utils/math';
import { hasHaptics, loop } from '../loopSdk';
import {
  isInstallerFlowActive,
  applyItemSlot,
  getSlotLayout,
  getItemSettledScale,
  applyItemTransform,
} from '../layout/ringLayout';
import { setHighlight } from '../items/highlight';
import { scrambleToText, getHomeIdleReticleText, setReticleEmptyCarrier } from '../render/matrixScramble';
import { quatInverse, quatMultiply, quatToDeviceRotation } from '../utils/quaternion';
import { setAttrIfChanged, setClassIfChanged, setStyleIfChanged } from '../layout/commit';

const OPTION_ACTIONS: readonly HomeOptionAction[] = ['remove_activity', 'edit_home_screen'];

// Orbit parameters for the options-menu input-indicator dot. The two focus
// rings (border-box sized) render as white lines at radii 132–134 (inner)
// and 147–149 (outer) — 13px of black gap between them. Radius 141 centers
// the 8px dot inside that gap with ~3px black breathing room on each side.
// Rest angle = 0 rad (3 o'clock, aligned with the options list). Phase
// [-1, 1] rotates the dot through ±π from rest — phase -1 sweeps 180°
// counter-clockwise (through 12 o'clock to 9 o'clock) and phase +1 sweeps
// 180° clockwise (through 6 o'clock to 9 o'clock), covering the full
// circle. The selection boundary stays at phase=0 (3 o'clock) regardless
// of range: upper half of the arc = first option, lower half = next.
const OPTION_DOT_ORBIT_RADIUS_PX = 141;
const OPTION_DOT_REST_ANGLE_RAD = 0;
const OPTION_DOT_ANGLE_RANGE_RAD = Math.PI;

// How long the "hold trigger + drag to edit" hint stays up before reverting
// to the idle [•] reticle. The user can short-circuit this by pressing the
// trigger on an icon — that immediately switches to the empty drag carrier.
const EDIT_HINT_REVERT_MS = 3000;
// Extra CSS px added to the inner reticle width on top of the dragged icon's
// visible diameter, so there's a small breathing gap between the icon and
// each bracket. The .inner element already has 2px padding on each side, so
// 0 here gives ~2-4px visual gap depending on the bracket glyph.
const RETICLE_CARRIER_PADDING_PX = 0;
let editHintRevertTimer: ReturnType<typeof setTimeout> | null = null;

function clearEditHintRevertTimer(): void {
  if (editHintRevertTimer) {
    clearTimeout(editHintRevertTimer);
    editHintRevertTimer = null;
  }
}

function scheduleEditHintRevert(): void {
  clearEditHintRevertTimer();
  editHintRevertTimer = setTimeout(() => {
    editHintRevertTimer = null;
    if (state.homeUI.mode !== 'edit_mode') return;
    if (state.homeUI.dragActive) return;
    scrambleToText('•');
  }, EDIT_HINT_REVERT_MS);
}

function getDragItemVisibleDiameter(item: Item): number {
  const sceneScale = Math.max(0.001, state.menuScale * state.worldZoom);
  const scale = Math.max(0.001, item.currentScale || 1);
  return item.size * scale * sceneScale;
}

function pulse(strength: number): void {
  if (!hasHaptics) return;
  loop!.haptics!.pulse(strength);
}

function getActiveItemCount(): number {
  let count = 0;
  for (const item of state.items) {
    if (!item || item.installState === 'inserting') continue;
    count += 1;
  }
  return count;
}

function getFocusWorldFromCamera(): { x: number; y: number } {
  const sceneScale = Math.max(0.001, state.menuScale * state.worldZoom);
  return {
    x: CX + state.cameraX / sceneScale,
    y: CY + state.cameraY / sceneScale,
  };
}

function sortHiddenItemsByDistance(target: Item): Item[] {
  return state.items
    .filter(item => !!item && item !== target && item.installState !== 'inserting')
    .sort((a, b) => {
      const adx = a.baseX - target.baseX;
      const ady = a.baseY - target.baseY;
      const bdx = b.baseX - target.baseX;
      const bdy = b.baseY - target.baseY;
      return Math.hypot(adx, ady) - Math.hypot(bdx, bdy);
    });
}

function captureOptionsSnapshot(): Array<{ uid: number; x: number; y: number; scale: number }> {
  const snapshot: Array<{ uid: number; x: number; y: number; scale: number }> = [];
  for (const item of state.items) {
    if (!item || item.installState === 'inserting') continue;
    snapshot.push({
      uid: item.uid,
      x: Number.isFinite(item.currentX) ? item.currentX : item.baseX,
      y: Number.isFinite(item.currentY) ? item.currentY : item.baseY,
      scale: Math.max(0.001, Number.isFinite(item.currentScale) ? item.currentScale : getItemSettledScale(item)),
    });
  }
  return snapshot;
}

function getSnapshotPose(item: Item): { uid: number; x: number; y: number; scale: number } | null {
  const snapshot = state.homeUI.optionsSnapshot;
  for (let i = 0; i < snapshot.length; i += 1) {
    const pose = snapshot[i]!;
    if (pose.uid === item.uid) return pose;
  }
  return null;
}

function clearItemUiMultipliers(): void {
  for (const item of state.items) {
    setClassIfChanged(item.el, 'edit-shake', false);
    setClassIfChanged(item.el, 'edit-dragging', false);
  }
}

function setRingGuidesVisible(visible: boolean): void {
  const guides = document.querySelectorAll('.ring-guide');
  guides.forEach(guide => {
    guide.classList.toggle('visible', visible);
  });
}

function isOptionsMode(mode = state.homeUI.mode): boolean {
  return mode === 'options_entering' || mode === 'options_menu' || mode === 'options_exiting';
}

function isOptionAvailable(action: HomeOptionAction, item: Item | null): boolean {
  switch (action) {
    case 'remove_activity':
      return !!item && (item.kind === 'activity' || item.kind === 'placeholder');
    case 'edit_home_screen':
      return true;
  }
}

function getVisibleOptionActions(item: Item | null): HomeOptionAction[] {
  return OPTION_ACTIONS.filter(action => isOptionAvailable(action, item));
}

function canRemoveTargetItem(item: Item | null): boolean {
  return isOptionAvailable('remove_activity', item);
}

// Direct-mapping selection: the input phase [-1, 1] is sliced into N equal
// arc zones (one per visible option). The dot's current phase picks the
// option whose zone it's in — "wherever the dot lands points to that
// option's text". Tie-break at exact boundaries to the lower (earlier)
// zone so entering the menu at phase=0 preserves the default first index.
function phaseToOptionIndex(phase: number, visibleLen: number): number {
  if (visibleLen <= 1) return 0;
  const zoneSize = 2 / visibleLen;
  for (let i = 0; i < visibleLen - 1; i += 1) {
    const upper = -1 + (i + 1) * zoneSize;
    if (phase <= upper) return i;
  }
  return visibleLen - 1;
}

// Inverse: given a target zone index, return the phase value at the center
// of that zone. Used by keyboard nudges to snap the dot into the selected
// option's zone instead of manipulating the index directly.
function optionIndexToCenterPhase(idx: number, visibleLen: number): number {
  if (visibleLen <= 1) return 0;
  const zoneSize = 2 / visibleLen;
  return -1 + zoneSize * (idx + 0.5);
}

// ── Connector geometry (L4 commit, no DOM reads) ─────────────────────────
// The connector path is a pure function of the visible-options configuration
// and the selected visible index. Rows above the selected one render at the
// unselected line-height; the selected row sits at selected line-height.
// No DOM reads, no getBoundingClientRect — all math from scene constants.
const CONNECTOR_OUTER_RING_RADIUS = 149;     // matches .home-options-ring-mid radius
const CONNECTOR_ANCHOR_GAP_PX = 6;           // gap between outer ring and line anchor
const CONNECTOR_BRACKET_GAP_PX = 6;          // gap between line tip and selected "[" bracket
const CONNECTOR_BRACKET_OFFSET_PX = 14;      // .home-option::before `left: -14px`
const CONNECTOR_MIN_HORIZ_LEN_PX = 10;       // keep horizontal segment visible even when dy is large
const CONNECTOR_ANCHOR_X = CX + CONNECTOR_OUTER_RING_RADIUS + CONNECTOR_ANCHOR_GAP_PX;
const CONNECTOR_ANCHOR_Y = CY;

function computeConnectorPath(visibleLen: number, selectedIndex: number): string {
  if (visibleLen <= 0) return 'M 0 0';
  const idx = clamp(selectedIndex, 0, visibleLen - 1);
  // Row heights: the selected row uses the taller selected line-height, all
  // others use the unselected line-height. The list is centered vertically
  // around OPTIONS_LIST_CENTER_Y_PX (CSS `translateY(-50%)` on the list).
  let totalHeight = 0;
  for (let i = 0; i < visibleLen; i += 1) {
    totalHeight += (i === idx)
      ? OPTIONS_OPTION_LINE_HEIGHT_SELECTED_PX
      : OPTIONS_OPTION_LINE_HEIGHT_UNSELECTED_PX;
  }
  totalHeight += Math.max(0, visibleLen - 1) * OPTIONS_OPTION_GAP_PX;
  let y = OPTIONS_LIST_CENTER_Y_PX - totalHeight / 2;
  for (let i = 0; i < idx; i += 1) {
    y += OPTIONS_OPTION_LINE_HEIGHT_UNSELECTED_PX + OPTIONS_OPTION_GAP_PX;
  }
  const textY = y + OPTIONS_OPTION_LINE_HEIGHT_SELECTED_PX / 2;
  const textX = OPTIONS_LIST_LEFT_PX;
  const tipX = textX - CONNECTOR_BRACKET_OFFSET_PX - CONNECTOR_BRACKET_GAP_PX;
  const dy = textY - CONNECTOR_ANCHOR_Y;
  const absDy = Math.abs(dy);
  const maxDiagDx = Math.max(0, tipX - CONNECTOR_ANCHOR_X - CONNECTOR_MIN_HORIZ_LEN_PX);
  const diagDx = Math.min(absDy, maxDiagDx);
  const elbowX = CONNECTOR_ANCHOR_X + diagDx;
  const elbowY = textY;
  return `M ${CONNECTOR_ANCHOR_X.toFixed(1)} ${CONNECTOR_ANCHOR_Y.toFixed(1)} L ${elbowX.toFixed(1)} ${elbowY.toFixed(1)} L ${tipX.toFixed(1)} ${elbowY.toFixed(1)}`;
}

const OPTION_ROW_ELEMENTS: Record<HomeOptionAction, HTMLElement> = {
  remove_activity: dom.homeOptionRemove,
  edit_home_screen: dom.homeOptionEdit,
};

export function getSelectedOptionRowElement(): HTMLElement | null {
  const ui = state.homeUI;
  const visibleActions = getVisibleOptionActions(ui.targetItem);
  const action = visibleActions[phaseToOptionIndex(ui.optionsInputPhase, visibleActions.length)];
  return action ? OPTION_ROW_ELEMENTS[action] : null;
}

function commitOverlayPresentation(): void {
  const ui = state.homeUI;
  const visible = ui.overlayOpacity > 0.001 && isOptionsMode();
  setClassIfChanged(dom.homeOptionsOverlay, 'visible', visible);
  setStyleIfChanged(
    dom.homeOptionsOverlay,
    'opacity',
    visible ? ui.overlayOpacity.toFixed(3) : '0',
  );
  const visibleActions = getVisibleOptionActions(ui.targetItem);
  const derivedIdx = phaseToOptionIndex(ui.optionsInputPhase, visibleActions.length);
  // Zone-cross haptic: only while the menu is interactive.
  if (ui.mode === 'options_menu' && derivedIdx !== ui.optionIndex) {
    ui.optionIndex = derivedIdx;
    pulse(0.11);
  }
  const selectedAction = visibleActions[derivedIdx] ?? null;
  for (const action of OPTION_ACTIONS) {
    const el = OPTION_ROW_ELEMENTS[action];
    setClassIfChanged(el, 'hidden', !visibleActions.includes(action));
    setClassIfChanged(el, 'selected', action === selectedAction);
  }
  if (visible && visibleActions.length > 0) {
    setAttrIfChanged(
      dom.homeOptionsConnectorPath,
      'd',
      computeConnectorPath(visibleActions.length, derivedIdx),
    );
  }
  // Orbital transform: `rotate(θ) translate(r, 0)` — primitive-wise CSS
  // interpolation keeps the dot on the radius-r arc during transitions
  // (Cartesian interpolation would cut chords and briefly pull the dot
  // inside the inner ring onto the icon).
  const dotPhase = clamp(ui.optionsInputPhase, -1, 1);
  const dotAngleDeg =
    (OPTION_DOT_REST_ANGLE_RAD + dotPhase * OPTION_DOT_ANGLE_RANGE_RAD) * (180 / Math.PI);
  setStyleIfChanged(
    dom.homeOptionsInputDot,
    'transform',
    `rotate(${dotAngleDeg.toFixed(1)}deg) translate(${OPTION_DOT_ORBIT_RADIUS_PX}px, 0)`,
  );
}

function getRankedHideAmount(item: Item, phase: number, reveal: boolean): number {
  const hiddenItems = state.homeUI.hiddenItems;
  const count = hiddenItems.length;
  if (count === 0) return 0;
  const rank = hiddenItems.indexOf(item);
  if (rank < 0) return 0;

  const stagger = count > 1 ? HOME_OPTIONS_HIDE_STAGGER_SPAN / (count - 1) : 0;
  const start = rank * stagger;
  const t = clamp((phase - start) / HOME_OPTIONS_HIDE_FADE_SPAN, 0, 1);
  const eased = smoothstep(t);
  return reveal ? 1 - eased : eased;
}

function getBloopScale(hiddenAmount: number): number {
  const p = clamp(hiddenAmount, 0, 1);
  if (p <= 0) return 1;
  const overshootWindow = 0.32;
  if (p < overshootWindow) {
    return lerp(1, 1.2, easeOutCubic(p / overshootWindow));
  }
  const t = clamp((p - overshootWindow) / (1 - overshootWindow), 0, 1);
  const base = lerp(1.2, 0, t);
  const damp = 1 - t;
  const bounce = Math.sin(t * Math.PI * 2.6) * damp * 0.22;
  return clamp(base + bounce, 0, 1.26);
}

function getBloopOpacity(hiddenAmount: number): number {
  const p = clamp(hiddenAmount, 0, 1);
  const fadeT = smoothstep(clamp((p - 0.32) / 0.68, 0, 1));
  return clamp(1 - fadeT, 0, 1);
}

function setModeNormal(): void {
  clearEditHintRevertTimer();
  const ui = state.homeUI;
  ui.mode = 'normal';
  ui.targetItem = null;
  ui.optionIndex = 0;
  ui.enterStartedAt = 0;
  ui.exitStartedAt = 0;
  ui.exitAction = null;
  ui.hiddenItems = [];
  ui.overlayOpacity = 0;
  ui.lineOpacity = 1;
  ui.zoomBlend = 0;
  ui.rollRefQuat = null;
  ui.lastTwistAt = 0;
  ui.twistNeutralLatched = true;
  ui.optionsInputPhase = 0;
  ui.dragActive = false;
  ui.dragItem = null;
  ui.dragTargetSlot = -1;
  ui.dragWorldX = 0;
  ui.dragWorldY = 0;
  ui.dragLockedScale = 0;
  ui.removingItem = null;
  ui.optionsSnapshot = [];
  filters.homeRoll.reset();
  clearItemUiMultipliers();
  commitOverlayPresentation();
  setStyleIfChanged(dom.lines, 'opacity', '1');
}

function setModeEdit(target: Item | null): void {
  const ui = state.homeUI;
  ui.mode = 'edit_mode';
  ui.targetItem = target;
  ui.optionIndex = 0;
  ui.enterStartedAt = 0;
  ui.exitStartedAt = 0;
  ui.exitAction = null;
  ui.hiddenItems = [];
  ui.overlayOpacity = 0;
  ui.lineOpacity = 1;
  ui.zoomBlend = 0;
  ui.rollRefQuat = null;
  ui.lastTwistAt = 0;
  ui.twistNeutralLatched = true;
  ui.optionsInputPhase = 0;
  ui.dragActive = false;
  ui.dragItem = null;
  ui.dragTargetSlot = -1;
  ui.dragLockedScale = 0;
  ui.removingItem = null;
  ui.optionsSnapshot = [];
  filters.homeRoll.reset();
  if (target) setHighlight(target, true);
  // Keep the reticle visible in edit mode and use it as a usage hint —
  // there's no other text affordance telling the user how to actually
  // pick up an icon.
  dom.reticle.classList.add('visible');
  scrambleToText(' hold trigger + drag to edit ');
  scheduleEditHintRevert();
  setRingGuidesVisible(true);
}

function applySequentialSlotsWithout(itemToSkip: Item): void {
  const ordered = state.items
    .filter(item => item && item.installState !== 'inserting' && item !== itemToSkip)
    .sort((a, b) => a.slotIndex - b.slotIndex);
  for (let i = 0; i < ordered.length; i += 1) {
    applyItemSlot(ordered[i]!, i, false);
  }
  state.lockHourItems = null;
}

function finalizeRemoveItem(item: Item): void {
  if (state.highlighted === item) {
    setHighlight(item, false);
    state.highlighted = null;
  }
  if (state.homeUI.targetItem === item) state.homeUI.targetItem = null;
  if (item.el && item.el.parentNode) item.el.parentNode.removeChild(item.el);
  state.items = state.items.filter(entry => entry !== item);
  if (state.installerItem === item) {
    state.installerItem = state.items.find(entry => entry.kind === 'installer') ?? null;
    state.installerSlotIndex = state.installerItem ? state.installerItem.slotIndex : -1;
  }
  const ordered = state.items
    .filter(entry => entry.installState !== 'inserting')
    .sort((a, b) => a.slotIndex - b.slotIndex);
  for (let i = 0; i < ordered.length; i += 1) {
    applyItemSlot(ordered[i]!, i, false);
  }
  state.lockHourItems = null;
}

function finishExit(): void {
  const ui = state.homeUI;
  const action = ui.exitAction;
  const removingItem = ui.removingItem;
  if (action === 'edit_home_screen') {
    setModeEdit(ui.targetItem);
    return;
  }
  if (action === 'remove_activity' && removingItem) {
    finalizeRemoveItem(removingItem);
  }
  const text = state.highlighted ? ` ${state.highlighted.name} ` : getHomeIdleReticleText();
  scrambleToText(text);
  dom.reticle.classList.add('visible');
  setRingGuidesVisible(true);
  setModeNormal();
  if (state.highlighted) setHighlight(state.highlighted, true);
}

export function finalizePendingExitCleanup(): void {
  const ui = state.homeUI;
  if (ui.mode !== 'options_exiting') return;
  if (ui.exitAction === 'remove_activity' && ui.removingItem) {
    finalizeRemoveItem(ui.removingItem);
  }
  ui.removingItem = null;
  ui.exitAction = null;
}

function beginExit(action: HomeOptionAction | 'cancel'): void {
  const ui = state.homeUI;
  if (!isOptionsMode()) return;
  if (ui.mode === 'options_exiting') return;

  ui.mode = 'options_exiting';
  ui.exitAction = action;
  ui.exitStartedAt = performance.now();
  ui.rollRefQuat = null;
  ui.lastTwistAt = 0;
  ui.twistNeutralLatched = true;
  ui.optionsInputPhase = 0;
  filters.homeRoll.reset();

  if (action === 'remove_activity' && ui.targetItem && canRemoveTargetItem(ui.targetItem)) {
    ui.removingItem = ui.targetItem;
    applySequentialSlotsWithout(ui.targetItem);
  } else {
    ui.removingItem = null;
  }

  const text = state.highlighted ? ` ${state.highlighted.name} ` : getHomeIdleReticleText();
  scrambleToText(text);
  dom.reticle.classList.add('visible');
  setRingGuidesVisible(true);
  if (state.highlighted) setHighlight(state.highlighted, true);
}

function getNearestSlotIndex(worldX: number, worldY: number): number {
  const count = Math.max(1, getActiveItemCount());
  let nearestSlot = 0;
  let minDist = Infinity;
  for (let i = 0; i < count; i += 1) {
    const slot = getSlotLayout(i);
    const dx = slot.x - worldX;
    const dy = slot.y - worldY;
    const dist = dx * dx + dy * dy;
    if (dist < minDist) {
      minDist = dist;
      nearestSlot = i;
    }
  }
  return nearestSlot;
}

function reorderForDrag(dragItem: Item, targetSlot: number): void {
  const ordered = state.items
    .filter(item => item && item.installState !== 'inserting')
    .sort((a, b) => a.slotIndex - b.slotIndex);
  const existingIndex = ordered.indexOf(dragItem);
  if (existingIndex < 0) return;
  const boundedTarget = clamp(targetSlot, 0, ordered.length - 1);
  if (boundedTarget === existingIndex) return;
  ordered.splice(existingIndex, 1);
  ordered.splice(boundedTarget, 0, dragItem);
  for (let i = 0; i < ordered.length; i += 1) {
    applyItemSlot(ordered[i]!, i, false);
  }
  state.lockHourItems = null;
  state.homeUI.dragTargetSlot = boundedTarget;
  pulse(0.08);
}

function updateUiVisualMultipliers(now: number): void {
  const ui = state.homeUI;
  const target = ui.targetItem;
  const opacityTargets = new Map<Item, number>();
  const scaleTargets = new Map<Item, number>();
  for (const item of state.items) {
    opacityTargets.set(item, 1);
    scaleTargets.set(item, 1);
    setClassIfChanged(item.el, 'edit-shake', false);
    if (!ui.dragActive || ui.dragItem !== item) {
      setClassIfChanged(item.el, 'edit-dragging', false);
    }
  }

  if (ui.mode === 'edit_mode') {
    for (const item of state.items) {
      if (!item || item.installState === 'inserting') continue;
      if (ui.dragActive && ui.dragItem === item) continue;
      setClassIfChanged(item.el, 'edit-shake', true);
    }
    if (ui.dragActive && ui.dragItem) {
      scaleTargets.set(ui.dragItem, 1.08);
    }
  } else if (isOptionsMode()) {
    let phase = 0;
    let reveal = false;
    if (ui.mode === 'options_entering' || ui.mode === 'options_menu') {
      // Mode flips to 'options_menu' once the visual zoom finishes (fast),
      // but the staggered hide-other-items animation runs over the full
      // HOME_OPTIONS_ENTER_MS window. Drive `phase` from enterStartedAt
      // for both modes so the fade keeps progressing after input goes live.
      const raw = clamp((now - ui.enterStartedAt) / HOME_OPTIONS_ENTER_MS, 0, 1);
      phase = easeOutBack(raw);
      reveal = false;
    } else if (ui.mode === 'options_exiting') {
      const raw = clamp((now - ui.exitStartedAt) / HOME_OPTIONS_EXIT_MS, 0, 1);
      phase = easeOutCubic(raw);
      reveal = true;
    }

    for (const item of state.items) {
      if (!item || item.installState === 'inserting') continue;
      if (target && item === target) continue;
      const hiddenAmount = getRankedHideAmount(item, phase, reveal);
      opacityTargets.set(item, getBloopOpacity(hiddenAmount));
      scaleTargets.set(item, getBloopScale(hiddenAmount));
      if (item === state.highlighted) setHighlight(item, false);
    }

    if (target) {
      opacityTargets.set(target, 1);
      scaleTargets.set(target, 1);
      if (target === state.highlighted) setHighlight(target, false);
    }

    if (ui.mode === 'options_exiting' && ui.exitAction === 'remove_activity' && ui.removingItem) {
      const raw = clamp((now - ui.exitStartedAt) / HOME_OPTIONS_EXIT_MS, 0, 1);
      // Reserve the first HOME_OPTIONS_REMOVE_DELAY_FRAC of the timeline
      // for the camera zoom-out so the user has a stable frame to register
      // *what* is being deleted before it starts shrinking.
      const span = 1 - HOME_OPTIONS_REMOVE_DELAY_FRAC;
      const removeT = clamp((raw - HOME_OPTIONS_REMOVE_DELAY_FRAC) / span, 0, 1);
      const eased = easeOutCubic(removeT);
      scaleTargets.set(ui.removingItem, clamp(1 - eased, 0, 1));
      // Match opacity to scale (no 1.2 multiplier) — the prior fast-fade
      // hit opacity 0 at ~half the timeline, masking the rest of the shrink.
      opacityTargets.set(ui.removingItem, clamp(1 - eased, 0, 1));
    }
  }

  const blend = 0.34;
  for (const item of state.items) {
    let targetOpacity = clamp(opacityTargets.get(item) ?? 1, 0, 1);
    let targetScale = Math.max(0, scaleTargets.get(item) ?? 1);
    // Highlight forces full scale/opacity — but NEVER for the item being
    // removed. During options_exiting the selector is unblocked, so if the
    // mouse is still hovering the dying icon it gets re-flagged as
    // highlighted on every frame, which would override (and silently
    // cancel) the shrink animation. Skip the override for removingItem.
    if (item === state.highlighted && item !== ui.removingItem) {
      targetOpacity = 1;
      targetScale = 1;
    }
    const currentOpacity = Number.isFinite(item.uiOpacityMul) ? item.uiOpacityMul : 1;
    const currentScale = Number.isFinite(item.uiScaleMul) ? item.uiScaleMul : 1;
    item.uiOpacityMul = lerp(currentOpacity, targetOpacity, blend);
    item.uiScaleMul = lerp(currentScale, targetScale, blend);
    if (Math.abs(item.uiOpacityMul - targetOpacity) < 0.002) item.uiOpacityMul = targetOpacity;
    if (Math.abs(item.uiScaleMul - targetScale) < 0.002) item.uiScaleMul = targetScale;
  }
}

function updateModeProgress(now: number): void {
  const ui = state.homeUI;
  if (ui.mode === 'normal') {
    ui.overlayOpacity = 0;
    ui.lineOpacity = 1;
    ui.zoomBlend = 0;
    return;
  }
  if (ui.mode === 'edit_mode') {
    ui.overlayOpacity = 0;
    ui.lineOpacity = 1;
    ui.zoomBlend = 0;
    return;
  }
  if (ui.mode === 'options_entering') {
    const rawZoom = clamp((now - ui.enterStartedAt) / HOME_OPTIONS_ZOOM_MS, 0, 1);
    const easedZoom = easeInOutCubic(rawZoom);
    ui.zoomBlend = easedZoom;
    ui.overlayOpacity = clamp((rawZoom - 0.18) / 0.72, 0, 1);
    ui.lineOpacity = clamp(1 - easedZoom, 0, 1);
    // Flip to 'options_menu' as soon as the visible zoom-in completes —
    // input is gated on this enum value, so coupling it to the longer
    // hide-other-items animation (HOME_OPTIONS_ENTER_MS) made the menu
    // appear interactive but be unresponsive for ~5.5 s. The hide phase
    // continues on its own timeline in updateUiVisualMultipliers.
    if (rawZoom >= 1) {
      ui.mode = 'options_menu';
      ui.zoomBlend = 1;
      ui.overlayOpacity = 1;
      ui.lineOpacity = 0;
      filters.homeRoll.reset();
      ui.rollRefQuat = null;
      ui.twistNeutralLatched = true;
    }
    return;
  }
  if (ui.mode === 'options_menu') {
    ui.zoomBlend = 1;
    ui.overlayOpacity = 1;
    ui.lineOpacity = 0;
    return;
  }
  if (ui.mode === 'options_exiting') {
    const raw = clamp((now - ui.exitStartedAt) / HOME_OPTIONS_EXIT_MS, 0, 1);
    const rawZoom = clamp((now - ui.exitStartedAt) / HOME_OPTIONS_ZOOM_OUT_MS, 0, 1);
    const easedZoom = easeOutCubic(rawZoom);
    ui.zoomBlend = clamp(1 - easedZoom, 0, 1);
    ui.overlayOpacity = clamp(1 - easedZoom, 0, 1);
    ui.lineOpacity = clamp(easedZoom, 0, 1);
    const completionRaw = ui.exitAction === 'edit_home_screen' ? rawZoom : raw;
    if (completionRaw >= 1) finishExit();
  }
}

export function isHomeUIActive(): boolean {
  return state.homeUI.mode !== 'normal';
}

export function isHomeOptionsFlowActive(): boolean {
  return isOptionsMode();
}

export function isHomeOptionsPoseFrozen(): boolean {
  return state.homeUI.mode === 'options_entering' || state.homeUI.mode === 'options_menu';
}

export function getHomeOptionsSnapshotScale(item: Item): number | null {
  if (!isOptionsMode()) return null;
  if (state.homeUI.mode === 'options_exiting') return null;
  const pose = getSnapshotPose(item);
  return pose ? pose.scale : null;
}

export function applyHomeOptionsSnapshotPoses(applyTransform: (item: Item) => void): void {
  if (!isHomeOptionsPoseFrozen()) return;
  const target = state.homeUI.targetItem;
  for (const item of state.items) {
    if (!item || item.installState === 'inserting') continue;
    const pose = getSnapshotPose(item);
    if (!pose) continue;
    const isTarget = !!(target && item === target);
    if (isTarget) {
      item.currentX = item.baseX;
      item.currentY = item.baseY;
    } else {
      item.currentX = pose.x;
      item.currentY = pose.y;
    }
    item.flowX = item.currentX;
    item.flowY = item.currentY;
    item.targetX = item.currentX;
    item.targetY = item.currentY;
    if (!isTarget) {
      item.currentScale = pose.scale;
    }
    applyTransform(item);
  }
}

export function isHomeUIBlockingSelector(): boolean {
  if (state.homeUI.mode === 'edit_mode' && !state.homeUI.dragActive) return false;
  if (state.homeUI.mode === 'options_exiting') return false;
  return state.homeUI.mode !== 'normal';
}

export function isHomeEditMode(): boolean {
  return state.homeUI.mode === 'edit_mode';
}

export function isHomeEditDragging(): boolean {
  return state.homeUI.mode === 'edit_mode' && state.homeUI.dragActive;
}

export function canStartHomeOptions(): boolean {
  if (state.appState !== 'home') return false;
  const mode = state.homeUI.mode;
  if (mode !== 'normal' && mode !== 'options_exiting') return false;
  if (isInstallerFlowActive()) return false;
  if (!state.highlighted) return false;
  if (state.highlighted.installState === 'inserting') return false;
  return true;
}

export function startHomeOptionsFromHighlight(): boolean {
  if (!canStartHomeOptions()) return false;
  finalizePendingExitCleanup();
  const target = state.highlighted!;
  const ui = state.homeUI;
  ui.mode = 'options_entering';
  ui.targetItem = target;
  ui.optionIndex = 0;
  ui.enterStartedAt = performance.now();
  ui.exitStartedAt = 0;
  ui.exitAction = null;
  ui.hiddenItems = sortHiddenItemsByDistance(target);
  ui.overlayOpacity = 0;
  ui.lineOpacity = 1;
  ui.zoomBlend = 0;
  ui.rollRefQuat = null;
  ui.lastTwistAt = 0;
  ui.twistNeutralLatched = true;
  ui.optionsInputPhase = 0;
  ui.dragActive = false;
  ui.dragItem = null;
  ui.dragTargetSlot = -1;
  ui.dragWorldX = target.currentX;
  ui.dragWorldY = target.currentY;
  ui.removingItem = null;
  ui.optionsSnapshot = captureOptionsSnapshot();
  filters.homeRoll.reset();
  setHighlight(target, false);
  dom.reticle.classList.remove('visible');
  setRingGuidesVisible(false);
  pulse(0.24);
  return true;
}

export function nudgeHomeOptionSelection(delta: number): boolean {
  const ui = state.homeUI;
  if (ui.mode !== 'options_menu') return false;
  const visibleActions = getVisibleOptionActions(ui.targetItem);
  if (visibleActions.length <= 1) return false;
  const currentIdx = phaseToOptionIndex(ui.optionsInputPhase, visibleActions.length);
  const targetIdx = clamp(currentIdx + Math.sign(delta), 0, visibleActions.length - 1);
  if (targetIdx === currentIdx) return false;
  ui.optionsInputPhase = optionIndexToCenterPhase(targetIdx, visibleActions.length);
  return true;
}

export function handleHomeBackAction(): boolean {
  const ui = state.homeUI;
  if (state.appState !== 'home') return false;
  if (ui.mode === 'edit_mode') {
    if (ui.dragActive) endHomeEditDrag();
    const text = state.highlighted ? ` ${state.highlighted.name} ` : getHomeIdleReticleText();
    scrambleToText(text);
    dom.reticle.classList.add('visible');
    setRingGuidesVisible(true);
    setModeNormal();
    pulse(0.16);
    return true;
  }
  if (ui.mode === 'options_entering' || ui.mode === 'options_menu') {
    beginExit('cancel');
    pulse(0.14);
    return true;
  }
  if (ui.mode === 'options_exiting') {
    finalizePendingExitCleanup();
    setModeNormal();
    pulse(0.14);
    return true;
  }
  return false;
}

export function handleHomeSecondarySelectAction(): boolean {
  const ui = state.homeUI;
  if (state.appState !== 'home') return false;
  if (ui.mode === 'options_menu') {
    const visibleActions = getVisibleOptionActions(ui.targetItem);
    const idx = phaseToOptionIndex(ui.optionsInputPhase, visibleActions.length);
    const action = visibleActions[idx];
    if (!action) {
      pulse(0.08);
      return true;
    }
    beginExit(action);
    pulse(0.2);
    return true;
  }
  if (ui.mode === 'options_entering') return true;
  if (ui.mode === 'options_exiting') return false;
  if (ui.mode === 'edit_mode') return beginHomeEditDrag();
  return false;
}

export function beginHomeEditDrag(): boolean {
  const ui = state.homeUI;
  if (ui.mode !== 'edit_mode') return false;
  if (ui.dragActive) return true;
  const item = state.highlighted ?? ui.targetItem;
  if (!item || item.installState === 'inserting') return false;
  ui.dragActive = true;
  ui.dragItem = item;
  ui.dragTargetSlot = item.slotIndex;
  ui.dragWorldX = item.currentX;
  ui.dragWorldY = item.currentY;
  // Lock the icon to whatever size it had the moment the user grabbed it,
  // so dragging across rings of different base scales doesn't make it
  // pop bigger or smaller.
  const grabbedScale = Number.isFinite(item.currentScale) && item.currentScale > 0.001
    ? item.currentScale
    : getItemSettledScale(item);
  ui.dragLockedScale = grabbedScale;
  setClassIfChanged(item.el, 'edit-dragging', true);
  // Drag short-circuits the hint timer: replace the "hold trigger…" text
  // with empty brackets sized to the icon's visible diameter.
  clearEditHintRevertTimer();
  setReticleEmptyCarrier(getDragItemVisibleDiameter(item) + RETICLE_CARRIER_PADDING_PX);
  pulse(0.16);
  return true;
}

export function endHomeEditDrag(): boolean {
  const ui = state.homeUI;
  if (ui.mode !== 'edit_mode') return false;
  if (!ui.dragActive) return false;
  if (ui.dragItem) setClassIfChanged(ui.dragItem.el, 'edit-dragging', false);
  ui.dragActive = false;
  ui.dragItem = null;
  ui.dragTargetSlot = -1;
  ui.dragLockedScale = 0;
  // Restore the idle reticle once the icon is dropped — same target as the
  // post-hint state so successive grabs always start from [•].
  scrambleToText('•');
  pulse(0.1);
  return true;
}

export function applyHomeEditDragAfterLayout(): void {
  const ui = state.homeUI;
  if (ui.mode !== 'edit_mode' || !ui.dragActive || !ui.dragItem) return;
  const focus = getFocusWorldFromCamera();
  const nextSlot = getNearestSlotIndex(focus.x, focus.y);

  // Snapshot apparent pixel size BEFORE reorderForDrag, because it may call
  // applyItemSlot which sets item.size + the element's width/height to the
  // new ring's slot size (a step change). Visible pixel size = item.size *
  // currentScale, so we compensate after the reorder to keep the visual
  // continuous, then lerp toward scale=1 (the new slot's natural fit).
  const sizeBefore = ui.dragItem.size;
  const apparentBefore = sizeBefore * Math.max(0.001, ui.dragItem.currentScale);

  reorderForDrag(ui.dragItem, nextSlot);

  if (ui.dragItem.size !== sizeBefore && ui.dragItem.size > 0.001) {
    // Ring (and therefore slot.size) changed this frame. Re-derive
    // currentScale so apparent pixel size doesn't jump.
    ui.dragItem.currentScale = apparentBefore / ui.dragItem.size;
  }
  // Smoothly approach scale=1 (natural fit for the current slot). Combined
  // with the compensation above this is what produces a continuous resize
  // when the drag crosses a ring boundary.
  ui.dragItem.currentScale = lerp(ui.dragItem.currentScale, 1, HOME_EDIT_DRAG_SCALE_LERP);
  if (Math.abs(ui.dragItem.currentScale - 1) < 0.002) {
    ui.dragItem.currentScale = 1;
  }
  ui.dragLockedScale = ui.dragItem.currentScale;

  ui.dragWorldX = lerp(ui.dragWorldX, focus.x, HOME_EDIT_DRAG_WORLD_LERP);
  ui.dragWorldY = lerp(ui.dragWorldY, focus.y, HOME_EDIT_DRAG_WORLD_LERP);

  ui.dragItem.currentX = ui.dragWorldX;
  ui.dragItem.currentY = ui.dragWorldY;
  ui.dragItem.flowX = ui.dragWorldX;
  ui.dragItem.flowY = ui.dragWorldY;
  ui.dragItem.targetX = ui.dragWorldX;
  ui.dragItem.targetY = ui.dragWorldY;
  applyItemTransform(ui.dragItem);
  // Keep the empty-bracket carrier sized to the icon's current visible
  // diameter — currentScale lerps toward 1.0 across ring crossings, so the
  // brackets glide along with it instead of snapping at the new ring scale.
  setReticleEmptyCarrier(getDragItemVisibleDiameter(ui.dragItem) + RETICLE_CARRIER_PADDING_PX);
}

export function updateHomeOptionTwistFromMotion(currentQuat: Quat, timestampSec: number): void {
  const ui = state.homeUI;
  if (ui.mode !== 'options_menu') return;
  if (!ui.rollRefQuat) {
    ui.rollRefQuat = currentQuat;
    filters.homeRoll.reset();
    return;
  }
  const relQuat = quatMultiply(quatInverse(ui.rollRefQuat), currentQuat);
  const { roll } = quatToDeviceRotation(relQuat);
  const rollDeg = filters.homeRoll.filter(roll * (180 / Math.PI), timestampSec);
  const absRoll = Math.abs(rollDeg);
  if (absRoll <= HOME_OPTIONS_TWIST_DEADZONE_DEG) {
    ui.optionsInputPhase = 0;
    return;
  }
  // Direct mapping: roll angle → normalized phase. Commit picks the option
  // whose zone the phase is in and fires a zone-cross haptic.
  ui.optionsInputPhase = clamp(rollDeg / HOME_OPTIONS_TWIST_THRESHOLD_DEG, -1, 1);
}

// ── L3: animate (pure, no DOM) ───────────────────────────────────────────
function animateHomeUI(now: number): void {
  if (state.appState !== 'home') return;
  updateModeProgress(now);
  updateUiVisualMultipliers(now);
}

// ── L4: commit (DOM writes only, skip-if-unchanged) ──────────────────────
function commitHomeUI(): void {
  if (state.appState !== 'home') return;
  commitOverlayPresentation();
  setStyleIfChanged(dom.lines, 'opacity', state.homeUI.lineOpacity.toFixed(3));
}

export function stepHomeUI(now: number): void {
  animateHomeUI(now);
  commitHomeUI();
}

export function applyHomeUIItemOpacity(): void {
  if (state.appState !== 'home') return;
  for (const item of state.items) {
    const opacity = clamp((item.visualOpacity || 1) * (item.uiOpacityMul || 1), 0, 1);
    setStyleIfChanged(item.el, 'opacity', opacity.toFixed(3));
  }
}

export function getHomeUIWorldZoomTarget(): number | null {
  if (state.appState !== 'home') return null;
  if (!isOptionsMode()) return null;
  const target = state.homeUI.targetItem;
  let optionsZoomTarget = HOME_OPTIONS_WORLD_ZOOM;
  if (target) {
    const pose = getSnapshotPose(target);
    const baseScale = pose ? pose.scale : Math.max(0.001, getItemSettledScale(target));
    const baseDiameter = Math.max(1, target.size * baseScale * Math.max(0.001, state.menuScale));
    const desiredDiameter = 232;
    optionsZoomTarget = clamp(desiredDiameter / baseDiameter, 1.2, 3.2);
  }
  return lerp(1, optionsZoomTarget, clamp(state.homeUI.zoomBlend, 0, 1));
}

export function getHomeUICameraOverride(): { x: number; y: number; smoothing: number } | null {
  if (state.appState !== 'home') return null;
  const ui = state.homeUI;
  if (!isOptionsMode()) return null;
  if (ui.mode === 'options_exiting') return null;
  const target = ui.targetItem ?? ui.removingItem;
  if (!target) return null;
  const sceneScale = Math.max(0.001, state.menuScale * state.worldZoom);
  return {
    x: (target.baseX - CX) * sceneScale,
    y: (target.baseY - CY) * sceneScale,
    smoothing: HOME_OPTIONS_CAMERA_SMOOTHING,
  };
}

export function resetHomeUIState(): void {
  setModeNormal();
}
